import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useMobile } from './hooks/useMobile';
import { useReducedMotion } from './hooks/useReducedMotion';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

gsap.registerPlugin(ScrollTrigger);

const badBadges = [
  'Missed Deadline',
  'Zero Engagement',
  'Over Budget',
  'Wrong Tone',
  'Inconsistent Posts',
  'Team Burnout',
];

const goodBadges = [
  '3.2x Engagement',
  'Always On-Brand',
  'Saves 20hrs/week',
  'Psychology-Backed',
  'Zero Missed Posts',
  'Grows With You',
];

const oldRoles = ['Strategist', 'Content Writer', 'Designer', 'Proofreader'];

export function ProblemSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useMobile();
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    if (prefersReduced || !sectionRef.current) return;

    const ctx = gsap.context(() => {
      gsap.from('.problem-header > *', {
        opacity: 0,
        y: 30,
        stagger: 0.1,
        duration: 0.7,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.problem-header',
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
      });
      // Panels use Framer Motion whileInView — no GSAP needed
    }, sectionRef);

    return () => ctx.revert();
  }, [isMobile, prefersReduced]);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-white"
      style={{
        paddingTop: 'clamp(80px, 10vh, 140px)',
        paddingBottom: 'clamp(80px, 10vh, 140px)',
      }}
    >
      <div className="relative z-10 mx-auto max-w-6xl px-6 lg:px-8">
        {/* Header — left-aligned for asymmetry */}
        <div className="problem-header mb-16 max-w-2xl">
          <p
            className="mb-4 font-mono uppercase text-steel-dark"
            style={{
              fontSize: 'clamp(0.875rem, 1vw, 1.1rem)',
              fontWeight: 600,
              letterSpacing: '0.15em',
            }}
          >
            The Problem We Solve
          </p>
          <h2
            className="mb-4 text-ink font-display"
            style={{
              fontSize: 'clamp(2rem, 4vw + 0.5rem, 3.75rem)',
              lineHeight: 1.1,
            }}
          >
            Marketing is <span className="text-cargo">High Effort</span>
          </h2>
          <p
            className="max-w-[55ch] text-steel-dark font-body"
            style={{
              fontSize: 'clamp(1.25rem, 1vw + 0.9rem, 1.5rem)',
              lineHeight: 1.65,
            }}
          >
            Five roles. Endless coordination. Still missing deadlines. Zupa replaces all of it.
          </p>
        </div>

        {/* Two-panel comparison */}
        <div className={cn('grid gap-6', isMobile ? 'grid-cols-1' : 'grid-cols-2')}>
          {/* The Old Way */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="chaos-panel p-8 lg:p-10 bg-white"
            style={{
              border: '1px solid rgba(239,68,68,0.25)',
            }}
          >
            <h3
              className="mb-2 text-ink font-display"
              style={{
                fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
              }}
            >
              The Old Way
            </h3>
            <div className="mb-6 flex flex-wrap items-baseline gap-4">
              <span
                className="font-display"
                style={{
                  fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                  color: '#DC2626',
                }}
              >
                $15,000+/month
              </span>
              <span
                className="text-steel-dark font-body"
                style={{
                  fontSize: '1.25rem',
                }}
              >
                10-14 days per campaign
              </span>
            </div>

            {/* Roles */}
            <div className="mb-6 space-y-2">
              {oldRoles.map((role) => (
                <div
                  key={role}
                  className="flex items-center gap-2 text-steel-dark font-body"
                  style={{
                    fontSize: '1.3rem',
                  }}
                >
                  <span className="text-[#DC2626]/70">x</span>
                  {role}
                </div>
              ))}
            </div>

            {/* Bad badges */}
            <div className="flex flex-wrap gap-2">
              {badBadges.map((badge) => (
                <span
                  key={badge}
                  className="px-4 py-2 text-base font-medium font-mono"
                  style={{
                    background: 'rgba(239, 68, 68, 0.08)',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    color: '#DC2626',
                  }}
                >
                  {badge}
                </span>
              ))}
            </div>
          </motion.div>

          {/* The Zupa Way */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="zupa-panel p-8 lg:p-10 bg-ink"
          >
            <h3
              className="mb-2 text-paper font-display"
              style={{
                fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
              }}
            >
              The Zupa Way
            </h3>
            <div className="mb-6 flex flex-wrap items-baseline gap-4">
              <span
                className="text-cargo font-display"
                style={{
                  fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                }}
              >
                From $49/month
              </span>
              <span
                className="text-steel font-body"
                style={{
                  fontSize: '1.25rem',
                }}
              >
                Minutes per campaign
              </span>
            </div>

            {/* Zupa advantage description */}
            <p
              className="mb-6 text-steel font-body"
              style={{
                fontSize: '1.3rem',
                lineHeight: 1.6,
              }}
            >
              One tool. One dashboard. Psychology-backed content for every platform, generated in seconds.
            </p>

            {/* Good badges */}
            <div className="flex flex-wrap gap-2">
              {goodBadges.map((badge) => (
                <span
                  key={badge}
                  className="px-4 py-2 text-base font-medium font-mono"
                  style={{
                    background: 'rgba(247, 246, 242, 0.08)',
                    border: '1px solid rgba(247, 246, 242, 0.2)',
                    color: '#F7F6F2',
                  }}
                >
                  {badge}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
